import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DateRangeControl, defaultRange } from '@/components/shared';
import { Env, getAdminApiUrl, ENV_COLORS } from '@/pages/Admin/shared';
import { useAttribution } from './api/useAttribution';
import type { TimelinePoint, Spike, CandidatePost } from './api/types';

const METRIC_LABELS: Record<Spike['metric'], string> = {
  ios_installs: 'iOS installs',
  signups: 'Signups',
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl backdrop-blur-sm">
      <div className="text-gray-400 mb-1">{label}</div>
      {payload.map((entry) => (
        <div key={entry.name} className="font-semibold" style={{ color: entry.color }}>
          {entry.name}: {entry.value.toLocaleString()}
        </div>
      ))}
    </div>
  );
}

function AttributionPage(): React.ReactElement {
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const apiUrl = getAdminApiUrl(selectedEnv);
  const [[from, to], setRange] = useState<[string | null, string | null]>(defaultRange);
  const { data, loading, error, refetch } = useAttribution(apiUrl, from, to);

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/socials" className="text-sm text-gray-500 hover:text-gray-300 transition-colors no-underline">
            Socials
          </Link>
          <span className="text-gray-700 mx-2">/</span>
          <span className="text-sm text-gray-400">Growth Attribution</span>
        </div>

        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl font-semibold text-white font-display tracking-tight">Growth Attribution</h1>
              <p className="text-xs text-gray-400 mt-0.5">Correlates Instagram engagement with iOS installs and signups.</p>
            </div>

            {/* Env switcher */}
            <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
              {(['dev', 'staging', 'prod'] as Env[]).map((env) => {
                const envApiUrl = getAdminApiUrl(env);
                const isConfigured = Boolean(envApiUrl);
                const isActive = selectedEnv === env;
                return (
                  <button
                    key={env}
                    onClick={() => isConfigured && setSelectedEnv(env)}
                    disabled={!isConfigured}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                      isActive
                        ? ENV_COLORS[env].pill
                        : isConfigured
                          ? 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-gray-700/50'
                          : 'border-transparent text-gray-700 cursor-not-allowed'
                    }`}
                  >
                    {env.toUpperCase()}
                  </button>
                );
              })}
            </div>

            {/* Prod warning badge */}
            {selectedEnv === 'prod' && (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                PRODUCTION
              </span>
            )}
          </div>

          <DateRangeControl from={from} to={to} onChange={(f, t) => setRange([f, t])} />
        </div>

        {/* Error state */}
        {error && (
          <div className="mb-8 bg-red-950/30 border border-red-800/40 rounded-xl p-4 flex items-center justify-between">
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={refetch}
              className="px-3 py-1.5 text-sm font-medium text-red-300 bg-red-900/40 rounded-lg hover:bg-red-900/60 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && data.timeline.length === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
            <svg className="w-12 h-12 text-gray-700 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            <p className="text-gray-400 text-sm">No attribution data yet — appears after the daily collectors have run.</p>
            <p className="text-gray-600 text-xs mt-1">Check back after midnight UTC when the analytics pipeline completes.</p>
          </div>
        )}

        {/* Dashboard content */}
        {!loading && !error && data.timeline.length > 0 && (
          <div className="space-y-6">

            {/* Timeline overlay chart */}
            <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-5">
              <div className="mb-4">
                <h2 className="text-white font-semibold text-base">Growth Timeline</h2>
                <p className="text-xs text-gray-400 mt-0.5">iOS installs, signups, and Instagram engagement over time.</p>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-5 mb-4">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-0.5 rounded-full bg-emerald-400 inline-block" />
                  <span className="text-xs text-gray-400">iOS installs</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-0.5 rounded-full bg-violet-400 inline-block" />
                  <span className="text-xs text-gray-400">Signups</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-0.5 rounded-full bg-pink-400 inline-block" />
                  <span className="text-xs text-gray-400">IG engagement</span>
                </div>
              </div>

              <div style={{ height: 340, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.timeline} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                    <XAxis
                      dataKey="metric_date"
                      stroke="#6b7280"
                      fontSize={11}
                      minTickGap={32}
                      tickFormatter={(val: string) => {
                        const d = new Date(val);
                        return `${d.getMonth() + 1}/${d.getDate()}`;
                      }}
                    />
                    <YAxis allowDecimals={false} stroke="#6b7280" fontSize={11} width={48} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="ios_installs"
                      name="iOS installs"
                      stroke="#34d399"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="signups"
                      name="Signups"
                      stroke="#a78bfa"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="ig_engagement"
                      name="IG engagement"
                      stroke="#f472b6"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Detected spikes panel */}
            <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-5">
              <div className="mb-4">
                <h2 className="text-white font-semibold text-base">Detected Spikes</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Days where installs or signups ran at least 50% above the average of the 7 days before. A day
                  needs those 7 days inside the selected range to be judged, so the first week of any range shows
                  no spikes.
                </p>
              </div>

              {data.spikes.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No spikes detected in this range.</p>
              ) : (
                <div className="space-y-4">
                  {data.spikes.map((spike: Spike, idx: number) => (
                    <div key={`${spike.metric}-${spike.metric_date}-${idx}`} className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                      {/* Spike header */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <div>
                          <span className="text-white font-semibold text-sm">
                            {METRIC_LABELS[spike.metric] ?? spike.metric} spike on {spike.metric_date}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-emerald-400">
                            +{spike.pct_increase}%
                          </span>
                          <span className="text-xs text-gray-500">
                            vs baseline {spike.baseline.toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-400 bg-gray-800 px-2 py-0.5 rounded-full">
                            {spike.value.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Candidate posts */}
                      {spike.candidate_posts.length === 0 ? (
                        <p className="text-xs text-gray-600">No candidate posts found for this spike.</p>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">Candidate posts</p>
                          {spike.candidate_posts.map((post: CandidatePost) => (
                            <div key={post.post_id} className="flex items-start justify-between gap-3 text-xs">
                              <div className="flex-1 min-w-0">
                                {post.permalink ? (
                                  <a
                                    href={post.permalink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-violet-400 hover:text-violet-300 transition-colors underline-offset-2 hover:underline truncate block"
                                  >
                                    {post.caption?.slice(0, 60) ?? post.post_id}
                                    {post.caption && post.caption.length > 60 ? '…' : ''}
                                  </a>
                                ) : (
                                  <span className="text-gray-400 truncate block">
                                    {post.caption?.slice(0, 60) ?? post.post_id}
                                    {post.caption && post.caption.length > 60 ? '…' : ''}
                                  </span>
                                )}
                              </div>
                              <span className="text-pink-400 font-medium whitespace-nowrap">
                                {post.engagement.toLocaleString()} engagement
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default AttributionPage;
